import React, { useMemo } from 'react';
import {
	Modal,
	TouchableOpacity,
	Text,
	Image,
	View,
	KeyboardAvoidingView,
	StyleSheet,
	useWindowDimensions,
} from 'react-native';
import type { ViewStyle } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Cta from 'src/presentation/components/atoms/Cta';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import Colors from 'src/core/utils/Colors';
import Images from 'src/core/utils/Images';

import type { ApiPageGallery } from 'src/core/models/api/ApiPage';

interface ChooseGalleryModalProps {
	galleries: ApiPageGallery[];
	modalVisible: boolean;
	onGalleryClick: (gallery: ApiPageGallery) => void;
	closeModal: () => void;
}

function ChooseGalleryModal({ galleries, modalVisible, onGalleryClick, closeModal }: ChooseGalleryModalProps) {
	const { height } = useWindowDimensions();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo(
		(): { backdrop: ViewStyle; sheet: ViewStyle } => ({
			backdrop: {
				maxHeight: height,
			},
			// Decision D8: modals stay inside the app's column. The dim backdrop still covers the
			// window, so only the sheet is constrained.
			sheet: {
				width: contentWidth,
			},
		}),
		[contentWidth, height],
	);

	const ListItem = (title: string, image: string | undefined, onClick: () => void, index: number) => {
		return (
			<TouchableOpacity key={index} style={styles.listItem} onPress={onClick}>
				<Image source={image ? { uri: image } : Images.placeholder} style={styles.listItemImage} />
				<Text>{title}</Text>
			</TouchableOpacity>
		);
	};

	return (
		<Modal animationType={'fade'} style={styles.modal} transparent={true} visible={modalVisible}>
			<KeyboardAvoidingView style={[styles.backdrop, sizedStyles.backdrop]}>
				<SafeAreaView edges={['bottom']} style={[styles.sheet, sizedStyles.sheet]}>
					<Text style={styles.title}>{Translations.titleChooseGallery}</Text>
					<Text style={styles.description}>{Translations.textChooseGalleryDescription}</Text>
					<View>
						{galleries.map((gallery, index) =>
							ListItem(gallery.name, gallery.data?.page_image, () => onGalleryClick(gallery), index),
						)}
					</View>
					<TouchableOpacity onPress={closeModal}>
						<Cta
							buttonStyle={'primary'}
							label={Translations.buttonClose}
							labelColor={Colors.white}
							style={styles.closeButton}
						/>
					</TouchableOpacity>
				</SafeAreaView>
			</KeyboardAvoidingView>
		</Modal>
	);
}

export default ChooseGalleryModal;

const styles = StyleSheet.create({
	modal: {
		minHeight: '100%',
		flex: 1,
	},
	backdrop: {
		backgroundColor: Colors.overlay50,
		flex: 1,
		minHeight: '100%',
		// Anchor the sheet to the bottom so the dim backdrop (not the white sheet) sits behind the status bar,
		// keeping the light status-bar icons readable.
		justifyContent: 'flex-end',
	},
	sheet: {
		alignSelf: 'center',
		backgroundColor: 'white',
		paddingBottom: Dimens.padding12,
	},
	title: {
		fontSize: Dimens.fontSize18,
		marginTop: Dimens.margin12,
		textAlign: 'center',
	},
	description: {
		fontSize: Dimens.fontSize14,
		marginVertical: Dimens.margin12,
		textAlign: 'center',
	},
	listItem: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: Dimens.padding12,
		paddingHorizontal: Dimens.padding12,
		borderTopWidth: 1,
		borderColor: Colors.overlay10,
	},
	listItemImage: {
		height: 48,
		width: 48,
		borderRadius: 4,
		marginRight: Dimens.margin12,
	},
	closeButton: {
		marginHorizontal: Dimens.margin15,
		marginTop: Dimens.margin12,
	},
});
